import * as Sentry from '@sentry/react';
import { type ReactNode, useEffect, useState } from 'react';

import session from 'auth/session';

// Establishes the platform session before anything else mounts. There is no logged-out
// state to render — no session means a redirect to the AS.
//
// Its own bare markup rather than LoadingScreen/ErrorScreen: those render Page, which
// renders Header, which links — and there is no router yet to link within.

type State =
  { status: 'loading' } | { status: 'redirecting' } | { status: 'error' } | { status: 'ready'; orgSlug: string };

interface Props {
  children: (orgSlug: string) => ReactNode;
}

export default function AuthGuard({ children }: Props) {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    session
      .start()
      .then((result) => {
        setState(result.status === 'ready' ? { status: 'ready', orgSlug: result.orgSlug } : { status: 'redirecting' });
      })
      .catch((error: unknown) => {
        Sentry.captureException(error);
        setState({ status: 'error' });
      });
  }, []);

  if (state.status === 'ready') return <>{children(state.orgSlug)}</>;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
      }}
    >
      {state.status === 'error' ? 'Could not load your session. Please reload.' : null}
      {state.status === 'redirecting' ? 'Signing in' : null}
      {state.status === 'loading' ? 'Authenticating' : null}
    </div>
  );
}
